import { ClipboardList, FilePlus2, Files, KanbanSquare, Layers, RefreshCw, Users } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import LanguageToggle from '../components/LanguageToggle';
import Sidebar from '../components/Sidebar';
import { useEffect, useState } from 'react';
import AlertModal from '../components/AlertModal';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { apiRequest } from '../services/api';
import { useLang } from '../LangContext';
import FormCreatePage from './admin/FormCreatePage';
import FormEditPage from './admin/FormEditPage';
import FormListPage from './admin/FormListPage';
import MoldTypesPage from './admin/MoldTypesPage';
import SubmissionPage from './admin/SubmissionPage';
import SubmissionKanbanPage from './admin/SubmissionKanbanPage';
import UserManagementPage from './admin/UserManagementPage';

export default function AdminDashboard() {
  const { locale: t } = useLang();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [moldTypes, setMoldTypes] = useState([]);
  const [alert, setAlert] = useState(null);

  async function loadData() {
    try {
      const [usersResult, formsResult, submissionsResult, statusesResult, moldTypesResult] = await Promise.all([
        apiRequest('/api/users'),
        apiRequest('/api/forms/admin'),
        apiRequest('/api/submissions/admin'),
        apiRequest('/api/statuses'),
        apiRequest('/api/moldtypes/admin'),
      ]);
      setUsers(usersResult);
      setForms(formsResult);
      setSubmissions(submissionsResult);
      setStatuses(statusesResult);
      setMoldTypes(moldTypesResult);
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  // Kurumsal admin teması body üzerinden kapsanır — portal ile body'ye açılan modallar da aynı temayı alır
  useEffect(() => {
    document.body.classList.add('admin-mode');
    return () => document.body.classList.remove('admin-mode');
  }, []);

  function notifySuccess(text) {
    setAlert({ type: 'success', message: text });
  }

  async function createUser(payload) {
    try {
      await apiRequest('/api/users', { method: 'POST', body: JSON.stringify(payload) });
      notifySuccess('Kullanıcı oluşturuldu.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function updateUser(userId, payload) {
    try {
      // Yeni endpoint'leri (\/details ve \/active) dene
      await apiRequest(`/api/users/${userId}/details`, {
        method: 'PATCH',
        body: JSON.stringify({ userName: payload.userName, email: payload.email, phone: payload.phone ?? null, address: payload.address ?? null, language: payload.language ?? null }),
      });
      await apiRequest(`/api/users/${userId}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: payload.isActive }),
      });
      notifySuccess('Kullanıcı güncellendi.');
      await loadData();
      return true;
    } catch (exception) {
      // Yeni PATCH endpoint'leri yoksa (404) geri uyumlu PUT ile devam et
      if (exception.status === 404) {
        try {
          await apiRequest(`/api/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify({ email: payload.email, isActive: payload.isActive }),
          });
          notifySuccess('Kullanıcı güncellendi.');
          await loadData();
          return true;
        } catch (fallback) {
          setAlert({ type: 'error', message: fallback.message });
          return false;
        }
      }
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function resetUserPassword(userId, newPassword) {
    try {
      await apiRequest(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      });
      notifySuccess('Şifre sıfırlandı.');
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function deleteUser(userId) {
    try {
      await apiRequest(`/api/users/${userId}`, { method: 'DELETE' });
      notifySuccess('Kullanıcı silindi.');
      await loadData();
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      throw exception;
    }
  }

  async function createForm(payload) {
    try {
      await apiRequest('/api/forms', { method: 'POST', body: JSON.stringify(payload) });
      notifySuccess('Form kaydedildi.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function updateForm(formId, payload) {
    try {
      await apiRequest(`/api/forms/${formId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...payload, isActive: true }),
      });
      notifySuccess('Form güncellendi. Atanmış kullanıcılar formun güncel halini görecek.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function deleteForm(formId) {
    try {
      await apiRequest(`/api/forms/${formId}`, { method: 'DELETE' });
      notifySuccess('Form ve bağlı tüm atamalar silindi.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  // Mevcut formu yeni bir isimle çoğaltır; alanlar, bölümler ve seçenekler kopyalanır, atamalar/gönderimler kopyalanmaz
  async function duplicateForm(formId, newName) {
    try {
      const body = JSON.stringify({ name: newName?.trim() || null });
      await apiRequest(`/api/forms/${formId}/duplicate`, { method: 'POST', body });
      notifySuccess('Form kopyalandı.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function createMoldType(payload) {
    try {
      const created = await apiRequest('/api/moldtypes', { method: 'POST', body: JSON.stringify(payload) });
      notifySuccess('Kalıp oluşturuldu.');
      await loadData();
      return created;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return null;
    }
  }

  async function updateMoldType(moldId, payload) {
    try {
      await apiRequest(`/api/moldtypes/${moldId}`, { method: 'PUT', body: JSON.stringify(payload) });
      notifySuccess('Kalıp güncellendi.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function deleteMoldType(moldId) {
    try {
      await apiRequest(`/api/moldtypes/${moldId}`, { method: 'DELETE' });
      notifySuccess('Kalıp silindi.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function linkMoldForm(moldId, language, formTemplateId) {
    try {
      await apiRequest(`/api/moldtypes/${moldId}/forms/${language}`, {
        method: 'PUT',
        body: JSON.stringify({ formTemplateId }),
      });
      notifySuccess('Form ilişkilendirildi.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function unlinkMoldForm(moldId, language) {
    try {
      await apiRequest(`/api/moldtypes/${moldId}/forms/${language}`, { method: 'DELETE' });
      notifySuccess('İlişki kaldırıldı.');
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function uploadMoldImage(file) {
    const formData = new FormData();
    formData.append('file', file, 'mold-cover.jpg');
    const response = await apiRequest('/api/moldtypes/image', { method: 'POST', body: formData });
    return response?.url || null;
  }

  // Sürükle-bırak sonrası kalıp sırasını kaydeder — her sürüklemede toast göstermeyi
  // önlemek için sessizce kaydeder, yalnızca hata olursa uyarır
  async function reorderMoldTypes(orderedIds) {
    try {
      await apiRequest('/api/moldtypes/reorder', {
        method: 'PUT',
        body: JSON.stringify({ orderedIds }),
      });
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      await loadData();
      return false;
    }
  }

  async function loadSubmission(id) {
    try {
      return await apiRequest(`/api/submissions/${id}`);
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return null;
    }
  }

  async function changeStatus(submissionId, statusId) {
    try {
      await apiRequest(`/api/submissions/${submissionId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ statusId: Number(statusId) }),
      });
      notifySuccess('Status güncellendi.');
      await loadData();
      return await loadSubmission(submissionId);
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return null;
    }
  }

  // Yanlış gönderilen formu tamamen siler; başarı durumunda true döner (çağıran modalı kapatır)
  async function deleteSubmission(submissionId) {
    try {
      const result = await apiRequest(`/api/submissions/${submissionId}`, { method: 'DELETE' });
      // 207: kayıt silindi ama bazı dosyalar S3'ten kaldırılamadı — uyar, akışı durdurma
      if (result?.errors?.length) {
        setAlert({ type: 'error', message: `${result.message} ${result.errors.join(', ')}` });
      } else {
        notifySuccess('Gönderim silindi.');
      }
      await loadData();
      return true;
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return false;
    }
  }

  async function clearSubmissionFiles(submissionId) {
    try {
      await apiRequest(`/api/submissions/${submissionId}/files`, { method: 'DELETE' });
      notifySuccess('Dosyalar silindi.');
      return await loadSubmission(submissionId);
    } catch (exception) {
      setAlert({ type: 'error', message: exception.message });
      return null;
    }
  }

  const pageProps = {
    users,
    forms,
    submissions,
    statuses,
    moldTypes,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    createForm,
    updateForm,
    deleteForm,
    duplicateForm,
    createMoldType,
    updateMoldType,
    deleteMoldType,
    linkMoldForm,
    unlinkMoldForm,
    uploadMoldImage,
    reorderMoldTypes,
    loadSubmission,
    changeStatus,
    clearSubmissionFiles,
    deleteSubmission,
    refresh: loadData,
  };

  const sections = [
    {
      label: t.sidebarAdmin.formManagement,
      items: [
        { to: '/admin/form-ekle', label: t.sidebarAdmin.addForm, icon: FilePlus2 },
        { to: '/admin/formlar', label: t.sidebarAdmin.listForms, icon: Files },
        { to: '/admin/kalip-yonetimi', label: t.sidebarAdmin.moldTypes, icon: Layers },
      ],
    },
    {
      label: t.sidebarAdmin.users,
      items: [
        { to: '/admin/kullanici-yonetimi', label: t.sidebarAdmin.userManagement, icon: Users },
      ],
    },
    {
      label: t.sidebarAdmin.data,
      items: [
        { to: '/admin/gonderimler', label: t.sidebarAdmin.submissions, icon: ClipboardList },
        { to: '/admin/kanban', label: t.sidebarAdmin.kanban, icon: KanbanSquare },
      ],
    },
  ];

  // Üst çubukta bulunulan sayfanın adı ve bağlı olduğu menü grubu gösterilir
  const isFormEdit = /^\/admin\/formlar\/[^/]+\/duzenle/.test(location.pathname);
  const currentItem = sections
    .flatMap((section) => section.items.map((item) => ({ ...item, section: section.label })))
    .find((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  const pageTitle = isFormEdit ? t.sidebarAdmin.editForm : currentItem?.label || t.topbar.adminPanel;
  const pageSection = isFormEdit ? t.sidebarAdmin.formManagement : currentItem?.section || '';

  return (
    <div className="admin-shell">
      <Sidebar brand="Atunears" logoSrc="/logo.png" sections={sections} />

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-title">
            <span className="admin-breadcrumb">
              {t.topbar.adminPanel}
              {pageSection && <span className="admin-breadcrumb-sep">/</span>}
              {pageSection}
            </span>
            <h1>{pageTitle}</h1>
          </div>
          <div className="topbar-actions">
            <LanguageToggle expanded />
            <ThemeToggle />
            <button className="btn btn-light" onClick={loadData}><RefreshCw /> {t.topbar.refresh}</button>
          </div>
        </header>

        <main className="admin-content">
          <AlertModal isOpen={Boolean(alert)} type={alert?.type || 'info'} message={alert?.message || ''} onClose={() => setAlert(null)} />
          <Routes>
            <Route index element={<Navigate to="gonderimler" replace />} />
            <Route path="form-ekle" element={<FormCreatePage {...pageProps} />} />
            <Route path="formlar" element={<FormListPage {...pageProps} />} />
            <Route path="formlar/:id/duzenle" element={<FormEditPage {...pageProps} />} />
            <Route path="kullanici-yonetimi" element={<UserManagementPage {...pageProps} />} />
            <Route path="kullanici-ekle" element={<Navigate to="/admin/kullanici-yonetimi" replace />} />
            <Route path="kullanicilar" element={<Navigate to="/admin/kullanici-yonetimi" replace />} />
            <Route path="kalip-yonetimi" element={<MoldTypesPage {...pageProps} />} />
            <Route path="gonderimler" element={<SubmissionPage {...pageProps} />} />
            <Route path="kanban" element={<SubmissionKanbanPage {...pageProps} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}